(() => {
  const doc = document;
  const body = doc.body;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const formatMoney = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: window.signalCurrency || 'GBP'
      }).format((cents || 0) / 100);
    } catch (error) {
      return `£${((cents || 0) / 100).toFixed(2)}`;
    }
  };

  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const loader = doc.querySelector('[data-signal-loader]');
  if (loader) {
    let seen = false;
    try {
      seen = sessionStorage.getItem('ark8-signal-seen') === 'true';
    } catch (error) {
      seen = false;
    }
    const enabled = loader.dataset.enabled === 'true';
    const delay = reduceMotion || seen || !enabled ? 0 : 900;
    window.setTimeout(() => {
      loader.classList.add('is-hidden');
      try {
        sessionStorage.setItem('ark8-signal-seen', 'true');
      } catch (error) {
        // Storage can be unavailable in strict privacy modes; the loader still closes.
      }
    }, delay);
  }

  const canAnimateReveals = !reduceMotion
    && body.dataset.signalMotion !== 'off'
    && !window.Shopify?.designMode;
  const revealObserver = canAnimateReveals && 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -22% 0px', threshold: 0.08 })
    : null;

  const initializeReveals = (root = doc) => {
    const reveals = root.matches?.('[data-reveal]')
      ? [root]
      : [...root.querySelectorAll('[data-reveal]')];

    reveals.forEach((element) => {
      if (revealObserver) {
        revealObserver.observe(element);
      } else {
        element.classList.add('is-revealed');
      }
    });
  };

  initializeReveals();
  doc.addEventListener('shopify:section:load', (event) => initializeReveals(event.target));

  let parallaxItems = [];
  let heroZooms = [];
  let scrollQuotes = [];
  let scrollEffectFrame = null;

  const refreshScrollEffects = () => {
    parallaxItems = [...doc.querySelectorAll('[data-parallax-item]')];
    heroZooms = [...doc.querySelectorAll('[data-hero-zoom]')];
    scrollQuotes = [...doc.querySelectorAll('[data-scroll-quote]')];
  };

  const updateScrollEffects = () => {
    scrollEffectFrame = null;
    const viewportHeight = window.innerHeight || doc.documentElement.clientHeight;
    const allowParallax = !reduceMotion && window.innerWidth >= 750 && body.dataset.signalMotion !== 'off';

    heroZooms.forEach((hero) => {
      const zoomAmount = Number(hero.dataset.heroZoom || 0) / 100;
      const lagAmount = Number(hero.dataset.contentLag || 0) / 100;
      if (reduceMotion || body.dataset.signalMotion === 'off') {
        hero.style.setProperty('--hero-media-scale', '1');
        hero.style.setProperty('--hero-content-lag-y', '0px');
        return;
      }
      const rect = hero.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, -rect.top / Math.max(1, rect.height)));
      hero.style.setProperty('--hero-media-scale', (1 + (progress * zoomAmount)).toFixed(4));
      hero.style.setProperty('--hero-content-lag-y', `${(progress * rect.height * lagAmount).toFixed(2)}px`);
    });

    parallaxItems.forEach((item) => {
      if (!allowParallax) {
        item.style.setProperty('--parallax-y', '0px');
        return;
      }
      const pair = item.closest('[data-parallax-pair]');
      if (!pair) return;
      const rect = pair.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > viewportHeight + 200) return;
      const distanceFromCentre = (viewportHeight / 2) - (rect.top + rect.height / 2);
      const progress = Math.max(-1, Math.min(1, distanceFromCentre / viewportHeight));
      const speed = Number(item.dataset.parallaxSpeed || 0);
      item.style.setProperty('--parallax-y', `${(progress * speed * 2).toFixed(2)}px`);
    });

    scrollQuotes.forEach((section) => {
      const shade = section.querySelector('[data-quote-shade]');
      const content = section.querySelector('[data-quote-content]');
      if (!shade || !content) return;

      if (reduceMotion || body.dataset.signalMotion === 'off') {
        shade.style.opacity = '0.58';
        content.style.opacity = '1';
        content.style.transform = 'none';
        return;
      }

      const rect = section.getBoundingClientRect();
      const scrollDistance = Math.max(1, rect.height - viewportHeight);
      const progress = Math.max(0, Math.min(1, -rect.top / scrollDistance));
      const quoteProgress = Math.max(0, Math.min(1, (progress - 0.24) / 0.58));
      const maxDarkness = Number(section.dataset.maxDarkness || 0.65);
      shade.style.opacity = String(progress * maxDarkness);
      content.style.opacity = String(quoteProgress);
      content.style.transform = `translateY(${((1 - quoteProgress) * 26).toFixed(2)}px)`;
    });
  };

  const requestScrollEffects = () => {
    if (scrollEffectFrame !== null) return;
    scrollEffectFrame = window.requestAnimationFrame(updateScrollEffects);
  };

  refreshScrollEffects();
  updateScrollEffects();
  window.addEventListener('scroll', requestScrollEffects, { passive: true });
  window.addEventListener('resize', requestScrollEffects);
  doc.addEventListener('shopify:section:load', () => {
    refreshScrollEffects();
    requestScrollEffects();
  });

  doc.querySelectorAll('[data-scroll-to]').forEach((control) => {
    control.addEventListener('click', () => {
      const target = doc.querySelector(control.dataset.scrollTo);
      if (target) target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });
    });
  });

  const menuButton = doc.querySelector('[data-menu-toggle]');
  const mobileMenu = doc.querySelector('[data-mobile-menu]');
  const closeMenu = () => {
    if (!menuButton || !mobileMenu) return;
    menuButton.setAttribute('aria-expanded', 'false');
    mobileMenu.setAttribute('aria-hidden', 'true');
    body.classList.remove('is-menu-open');
  };
  if (menuButton && mobileMenu) {
    menuButton.addEventListener('click', () => {
      const isOpen = menuButton.getAttribute('aria-expanded') === 'true';
      menuButton.setAttribute('aria-expanded', String(!isOpen));
      mobileMenu.setAttribute('aria-hidden', String(isOpen));
      body.classList.toggle('is-menu-open', !isOpen);
    });
    mobileMenu.querySelectorAll('a').forEach((link) => link.addEventListener('click', closeMenu));
  }

  const drawer = doc.querySelector('[data-cart-drawer]');
  let lastCartTrigger = null;

  const openCart = (trigger) => {
    if (!drawer) return;
    lastCartTrigger = trigger || doc.activeElement;
    drawer.setAttribute('aria-hidden', 'false');
    body.classList.add('is-cart-open');
    drawer.querySelector('[data-cart-close]')?.focus();
  };

  const closeCart = () => {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    body.classList.remove('is-cart-open');
    lastCartTrigger?.focus?.();
  };

  doc.querySelectorAll('[data-cart-open]').forEach((button) => {
    button.addEventListener('click', (event) => {
      if (body.dataset.cartMode === 'page') return;
      event.preventDefault();
      openCart(button);
    });
  });
  drawer?.querySelectorAll('[data-cart-close]').forEach((button) => button.addEventListener('click', closeCart));

  const renderCart = (cart) => {
    if (!drawer) return;
    doc.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = cart.item_count;
    });

    const container = drawer.querySelector('[data-cart-items]');
    const footer = drawer.querySelector('[data-cart-footer]');
    const total = drawer.querySelector('[data-cart-total]');
    if (!container) return;

    if (!cart.items.length) {
      container.innerHTML = '<p class="cart-drawer__empty">Your bag is empty.</p>';
      if (footer) footer.hidden = true;
      return;
    }

    container.innerHTML = cart.items.map((item) => `
      <article class="cart-line" data-line-key="${escapeHtml(item.key)}">
        <a class="cart-line__image" href="${escapeHtml(item.url)}">
          ${item.image ? `<img src="${escapeHtml(item.image)}${item.image.includes('?') ? '&' : '?'}width=280" alt="${escapeHtml(item.product_title)}" width="280" height="320">` : ''}
        </a>
        <div class="cart-line__content">
          <a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
          ${item.variant_title ? `<span>${escapeHtml(item.variant_title)}</span>` : ''}
          <span>${formatMoney(item.final_line_price)}</span>
          <div class="cart-line__actions">
            <label><span class="visually-hidden">Quantity</span><input type="number" min="0" value="${item.quantity}" data-cart-quantity data-key="${escapeHtml(item.key)}"></label>
            <button type="button" class="text-button" data-cart-remove data-key="${escapeHtml(item.key)}">Remove</button>
          </div>
        </div>
      </article>
    `).join('');

    if (total) total.textContent = formatMoney(cart.total_price);
    if (footer) footer.hidden = false;
    bindCartChanges();
  };

  const getCart = async () => {
    const response = await fetch(`${window.signalRoutes.cart}.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to load cart');
    return response.json();
  };

  const changeCart = async (key, quantity) => {
    const response = await fetch(`${window.signalRoutes.cartChange}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: Number(quantity) })
    });
    if (!response.ok) throw new Error('Unable to update cart');
    renderCart(await response.json());
  };

  const bindCartChanges = () => {
    drawer?.querySelectorAll('[data-cart-remove]').forEach((button) => {
      button.addEventListener('click', () => changeCart(button.dataset.key, 0));
    });
    drawer?.querySelectorAll('[data-cart-quantity]').forEach((input) => {
      input.addEventListener('change', () => changeCart(input.dataset.key, input.value));
    });
  };
  bindCartChanges();

  doc.querySelectorAll('[data-product-form]').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      if (!drawer || body.dataset.cartMode === 'page') return;
      event.preventDefault();
      const submit = form.querySelector('[type="submit"]');
      const original = submit?.textContent;
      if (submit) {
        submit.disabled = true;
        submit.textContent = 'Adding…';
      }
      try {
        const response = await fetch(`${window.signalRoutes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.description || 'Unable to add this item');
        }
        renderCart(await getCart());
        openCart(submit);
      } catch (error) {
        const message = form.querySelector('[data-product-error]');
        if (message) {
          message.textContent = error.message;
          message.hidden = false;
        }
      } finally {
        if (submit) {
          submit.disabled = false;
          submit.textContent = original;
        }
      }
    });
  });

  doc.querySelectorAll('[data-product-root]').forEach((root) => {
    const json = root.querySelector('[data-product-json]');
    const form = root.querySelector('[data-product-form]');
    if (!json || !form) return;
    const product = JSON.parse(json.textContent);
    const variantInput = form.querySelector('[name="id"]');
    const price = root.querySelector('[data-product-price]');
    const sku = root.querySelector('[data-product-sku]');
    const submit = form.querySelector('[type="submit"]');
    const status = root.dataset.productStatus;

    const updateVariant = () => {
      const selected = [...form.querySelectorAll('[data-option-position]')].map((group) => {
        return group.querySelector('input:checked')?.value;
      });
      const variant = product.variants.find((candidate) => {
        return candidate.options.every((option, index) => option === selected[index]);
      });

      if (variant && variantInput) variantInput.value = variant.id;
      if (variant && price) price.textContent = formatMoney(variant.price);
      if (sku) sku.textContent = variant?.sku || '';

      if (!submit) return;
      const canBuy = status === 'out-now' && variant?.available;
      submit.disabled = !canBuy;
      submit.textContent = status === 'coming-soon'
        ? 'Coming soon'
        : status === 'out-of-stock' || (variant && !variant.available)
          ? 'SOLD OUT'
          : variant
            ? 'Add to bag'
            : 'Unavailable';
    };

    form.querySelectorAll('input[type="radio"]').forEach((input) => input.addEventListener('change', updateVariant));
    updateVariant();
  });

  doc.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeCart();
      closeMenu();
    }
  });
})();
